import React, { useEffect, useMemo, useState } from "react";
import { cleanEventStats, getNumberOfDeliveredPhotos, getNumberOfIndexedFaces, getNumberOfPhotos, getNumberOfSelfies, getNumberOfUsersAccessByCode, getNumberOfUsersWithMatch, getNumberOfUniquesSharesByEvent, getNumberOfUniquesViewsByEvent, getNumberOfGlobalViewsByEvent, getPhotosByUser, getNumberOfOrders, getNumberOfPackOrders, getTotalAmountOrders, getTotalAmountPackOrders, getSoldPhotos } from "../../../redux/actions/stats";
import { updateEvent } from "../../../redux/actions/events";
import { withRouter, useParams, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import PhotosByUserModal from "../../../components/events/PhotosByUserModal";
import { useTranslation } from "react-i18next";
import { formatCount, formatPercent, getOrderNumber } from "../../../helpers/functions";
import axiosInstance from "../../../services/api";
import { RevButton, RevCard, RevChip, RevEmpty, RevFigure, RevKvRow, RevSkeleton } from "../../../components/ds";

const DEFAULT_MANUAL_VALUE = 100;
const FUNNEL_BAR_MAX_PX = 96;
const FUNNEL_BAR_MIN_PX = 6;

const StatItem = ({ label, value, helper, onClick }) => (
    <div
        className={`rev-stat${onClick ? " rev-stat--link" : ""}`}
        onClick={onClick}
    >
        <div className="rev-stat__k">{label}</div>
        <RevFigure as="div" size="xl">
            {value ?? <span className="rev-text-muted">—</span>}
        </RevFigure>
        {helper && <div className="rev-stat__pie">{helper}</div>}
    </div>
);

const EventStats = ({ ...props }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [modal, setModal] = useState(false);
    const [registeredInput, setRegisteredInput] = useState(String(DEFAULT_MANUAL_VALUE));
    const [attendeesInput, setAttendeesInput] = useState(String(DEFAULT_MANUAL_VALUE));
    const [editingRegistered, setEditingRegistered] = useState(false);
    const [editingAttendees, setEditingAttendees] = useState(false);
    const [savingRegistered, setSavingRegistered] = useState(false);
    const [savingAttendees, setSavingAttendees] = useState(false);
    const [recentOrders, setRecentOrders] = useState([]);
    const [recentOrdersLoading, setRecentOrdersLoading] = useState(false);
    const [recentOrdersError, setRecentOrdersError] = useState(false);
    const { id } = useParams();

    useEffect(() => {
        if (id) {
            if (!props.numberOfDeliveredPhotos) props.getNumberOfDeliveredPhotos(id);
            if (!props.numberOfIndexedFaces) props.getNumberOfIndexedFaces(id);
            if (!props.numberOfPhotos) props.getNumberOfPhotos(id);
            if (!props.numberOfSelfies) props.getNumberOfSelfies(id);
            if (!props.numberOfUsersAccessByCode) props.getNumberOfUsersAccessByCode(id);
            if (!props.numberOfUsersWithMatch) props.getNumberOfUsersWithMatch(id);
            if (!props.numberOfUniquesSharesByEvent) props.getNumberOfUniquesSharesByEvent(id);
            if (!props.numberOfUniquesViewsByEvent) props.getNumberOfUniquesViewsByEvent(id);
            if (!props.numberOfGlobalViewsByEvent) props.getNumberOfGlobalViewsByEvent(id);
            if (!props.numberOfOrders) props.getNumberOfOrders(id);
            if (!props.numberOfPackOrders) props.getNumberOfPackOrders(id);
            if (!props.totalAmountOrders) props.getTotalAmountOrders(id);
            if (!props.totalAmountPackOrders) props.getTotalAmountPackOrders(id);
            if (!props.soldPhotos) props.getSoldPhotos(id);
        }
        return () => {
            props.cleanEventStats();
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (props.selectedEvent?.inscritos !== undefined) {
            setRegisteredInput(String(props.selectedEvent.inscritos));
        }
        if (props.selectedEvent?.asistentes !== undefined) {
            setAttendeesInput(String(props.selectedEvent.asistentes));
        }
    }, [props.selectedEvent]);

    useEffect(() => {
        if (!id) return;
        setRecentOrdersLoading(true);
        setRecentOrdersError(false);
        axiosInstance.get(`/orders-payments/${id}?limit=5`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : [];
                const sorted = [...data].sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
                setRecentOrders(sorted);
            })
            .catch(() => {
                setRecentOrdersError(true);
            })
            .finally(() => {
                setRecentOrdersLoading(false);
            });
    }, [id]);


    const locale = t('locale', { returnObjects: true }) || 'es-ES';
    const decimalFormatter = useMemo(() => new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [locale]);
    const currencyFormatter = useMemo(() => new Intl.NumberFormat(locale, { style: 'currency', currency: props.user?.currency || 'EUR' }), [locale, props.user?.currency]);

    const toNumberOrUndefined = (value) => (typeof value === "number" && !Number.isNaN(value) ? value : undefined);
    const safeNumber = (value) => (typeof value === "number" && !Number.isNaN(value)) ? value : 0;
    const parseInputNumber = (value) => {
        if (value === "" || value === null || value === undefined) return 0;
        const parsed = parseFloat(value);
        if (!Number.isFinite(parsed) || parsed < 0) return 0;
        return parsed;
    };

    const formatInteger = (value, isLoading = false) => {
        if (isLoading && (value === undefined || value === null)) {
            return <RevSkeleton kind="cifra" />;
        }
        return (typeof value === "number" && !Number.isNaN(value)
            ? formatCount(Math.round(value), locale)
            : <span className="rev-text-muted">—</span>);
    };

    const formatCurrency = (value, isLoading = false) => {
        if (isLoading && (value === undefined || value === null)) {
            return <RevSkeleton kind="cifra" />;
        }
        return (typeof value === "number" && !Number.isNaN(value)
            ? currencyFormatter.format(value)
            : <span className="rev-text-muted">—</span>);
    };

    const percentOf = (value, total) => {
        if (!total || total <= 0) return formatPercent(0, locale);
        return formatPercent(value / total, locale);
    };

    const averagePer = (value, total, label) => {
        if (!total || total <= 0) return `0 ${label}`;
        return `${decimalFormatter.format(value / total)} ${label}`;
    };

    const registeredValue = parseInputNumber(registeredInput);
    const attendeesValue = parseInputNumber(attendeesInput);
    const accessesRaw = toNumberOrUndefined(props.numberOfUsersAccessByCode);
    const accessesValue = safeNumber(props.numberOfUsersAccessByCode);
    const matchesRaw = toNumberOrUndefined(props.numberOfUsersWithMatch);
    const matchesValue = safeNumber(props.numberOfUsersWithMatch);
    const selfiesRaw = toNumberOrUndefined(props.numberOfSelfies);
    const selfiesValue = safeNumber(props.numberOfSelfies);
    const uploadedPhotosRaw = toNumberOrUndefined(props.numberOfPhotos);
    const uploadedPhotos = safeNumber(props.numberOfPhotos);
    const deliveredPhotosRaw = toNumberOrUndefined(props.numberOfDeliveredPhotos);
    const deliveredPhotos = safeNumber(props.numberOfDeliveredPhotos);
    const numberOfOrders = toNumberOrUndefined(props.numberOfOrders);
    const numberOfPackOrders = toNumberOrUndefined(props.numberOfPackOrders);
    const totalAmountOrders = toNumberOrUndefined(props.totalAmountOrders);
    const totalAmountPackOrders = toNumberOrUndefined(props.totalAmountPackOrders);
    const avgTicketValue = (numberOfOrders && numberOfOrders > 0 && totalAmountOrders !== undefined)
        ? totalAmountOrders / numberOfOrders
        : undefined;

    const handleManualChange = (setter) => (event) => {
        const { value } = event.target;
        setter(value);
    };

    const handleEditRegistered = () => {
        setEditingRegistered(true);
    };

    const handleEditAttendees = () => {
        setEditingAttendees(true);
    };

    const handleSaveRegistered = async () => {
        if (props.selectedEvent && props.selectedEvent.id) {
            setSavingRegistered(true);
            try {
                const event = { ...props.selectedEvent };
                event.inscritos = parseInputNumber(registeredInput);
                await props.updateEvent(event);
                setEditingRegistered(false);
            } catch (error) {
                console.error('Error saving inscritos:', error);
            } finally {
                setSavingRegistered(false);
            }
        } else {
            setEditingRegistered(false);
        }
    };

    const handleSaveAttendees = async () => {
        if (props.selectedEvent && props.selectedEvent.id) {
            setSavingAttendees(true);
            try {
                const event = { ...props.selectedEvent };
                event.asistentes = parseInputNumber(attendeesInput);
                await props.updateEvent(event);
                setEditingAttendees(false);
            } catch (error) {
                console.error('Error saving asistentes:', error);
            } finally {
                setSavingAttendees(false);
            }
        } else {
            setEditingAttendees(false);
        }
    };

    const handleCancelRegistered = () => {
        if (props.selectedEvent?.inscritos !== undefined) {
            setRegisteredInput(String(props.selectedEvent.inscritos));
        } else {
            setRegisteredInput(String(DEFAULT_MANUAL_VALUE));
        }
        setEditingRegistered(false);
    };

    const handleCancelAttendees = () => {
        if (props.selectedEvent?.asistentes !== undefined) {
            setAttendeesInput(String(props.selectedEvent.asistentes));
        } else {
            setAttendeesInput(String(DEFAULT_MANUAL_VALUE));
        }
        setEditingAttendees(false);
    };


    const onSetModal = () => {
        if (!modal) props.getPhotosByUser(id);
        setModal(!modal);
    }

    const handleNavigateToPayments = () => {
        history.push(`/events/${id}/payments`);
    }

    const funnelSteps = [
        {
            key: 'registered',
            label: t('eventStats.funnel.registered'),
            raw: registeredValue,
            prevSafe: null,
        },
        {
            key: 'attendees',
            label: t('eventStats.funnel.attendees'),
            raw: attendeesValue,
            prevSafe: registeredValue,
            ofLabel: t('eventStats.funnel.ofRegistered'),
        },
        {
            key: 'access',
            label: t('eventStats.funnel.access'),
            raw: accessesRaw,
            isLoading: props.loadingStats,
            prevSafe: attendeesValue,
            ofLabel: t('eventStats.funnel.ofAttendees'),
        },
        {
            key: 'match',
            label: t('eventStats.funnel.match'),
            raw: matchesRaw,
            isLoading: props.loadingStats,
            prevSafe: accessesValue,
            ofLabel: t('eventStats.funnel.ofAccess'),
        },
    ];
    funnelSteps.push({
        key: 'orders',
        label: t('eventStats.funnel.orders'),
        raw: numberOfOrders,
        isLoading: props.loadingStats,
        prevSafe: matchesValue,
        ofLabel: t('eventStats.funnel.ofUsers'),
        tone: 'ok',
    });

    const funnelMax = Math.max(1, ...funnelSteps.map((step) => safeNumber(step.raw)));
    const funnelBarHeightPx = (raw) => Math.max(FUNNEL_BAR_MIN_PX, Math.round((safeNumber(raw) / funnelMax) * FUNNEL_BAR_MAX_PX));

    return (
        <div className="rev-stats">
            <PhotosByUserModal users={props?.photosByUser} modal={modal} setModal={onSetModal} loading={props.loadingStats} />

            <RevCard titleAs="h3" title={t('eventStats.funnel.title')} sub={t('eventStats.funnel.subtitle')}
                foot={<span className="rev-help">{t('eventStats.funnel.foot')}</span>}>
                <div className="rev-funnel">
                    {funnelSteps.map((step, idx) => {
                        const heightPx = funnelBarHeightPx(step.raw);
                        const heightPct = (heightPx / FUNNEL_BAR_MAX_PX) * 100;
                        const pct = (step.prevSafe !== null && step.prevSafe > 0)
                            ? percentOf(safeNumber(step.raw), step.prevSafe)
                            : null;
                        return (
                            <div className="rev-funnel__step" key={step.key}>
                                <div className="rev-funnel__barwrap">
                                    <svg className="rev-funnel__barsvg" viewBox="0 0 100 100" preserveAspectRatio="none" role="presentation" aria-hidden="true">
                                        <rect
                                            x="0"
                                            y={100 - heightPct}
                                            width="100"
                                            height={heightPct}
                                            rx="4"
                                            className={`rev-funnel__bar--${idx + 1}`}
                                        />
                                    </svg>
                                </div>
                                <RevFigure as="div" size="md">{formatInteger(step.raw, step.isLoading)}</RevFigure>
                                <div className="rev-funnel__label">{step.label}</div>
                                <div className={`rev-funnel__foot${step.tone === 'ok' ? ' rev-funnel__foot--ok' : ''}`}>
                                    {step.prevSafe === null
                                        ? t('eventStats.funnel.ownData')
                                        : (pct
                                            ? <><span className="rev-num">{pct}</span> {step.ofLabel}</>
                                            : t('eventStats.funnel.noData'))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </RevCard>

            <RevCard titleAs="h3" title={t('eventStats.registration.title')} sub={t('eventStats.registration.subtitle')}>
                <div className="rev-stat-grid">
                    <div className="rev-stat">
                        <div className="rev-stat__k">{t('eventStats.registration.registered')}</div>
                        {editingRegistered ? (
                            <div className="rev-stat__edit">
                                <input
                                    type="number"
                                    min="0"
                                    className="rev-input rev-num"
                                    value={registeredInput}
                                    onChange={handleManualChange(setRegisteredInput)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveRegistered();
                                        } else if (e.key === 'Escape') {
                                            handleCancelRegistered();
                                        }
                                    }}
                                    disabled={savingRegistered}
                                    autoFocus
                                />
                                <RevButton variant="secondary" icon onClick={handleSaveRegistered} disabled={savingRegistered}
                                    aria-label={t('eventStats.registration.saveAria')}>
                                    <span className="material-icons rev-icon-sm">{savingRegistered ? 'hourglass_empty' : 'check'}</span>
                                </RevButton>
                                <RevButton variant="secondary" icon onClick={handleCancelRegistered} disabled={savingRegistered}
                                    aria-label={t('eventStats.registration.cancelAria')}>
                                    <span className="material-icons rev-icon-sm">close</span>
                                </RevButton>
                            </div>
                        ) : (
                            <div className="rev-stat__editrow">
                                <RevFigure size="xl">{formatInteger(registeredValue)}</RevFigure>
                                <RevButton variant="secondary" icon onClick={handleEditRegistered}
                                    aria-label={t('eventStats.registration.editAria')}>
                                    <span className="material-icons rev-icon-sm">edit</span>
                                </RevButton>
                            </div>
                        )}
                        <div className="rev-stat__pie">{t('eventStats.registration.registeredHelp')}</div>
                    </div>
                    <div className="rev-stat">
                        <div className="rev-stat__k">{t('eventStats.registration.attendees')}</div>
                        {editingAttendees ? (
                            <div className="rev-stat__edit">
                                <input
                                    type="number"
                                    min="0"
                                    className="rev-input rev-num"
                                    value={attendeesInput}
                                    onChange={handleManualChange(setAttendeesInput)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveAttendees();
                                        } else if (e.key === 'Escape') {
                                            handleCancelAttendees();
                                        }
                                    }}
                                    disabled={savingAttendees}
                                    autoFocus
                                />
                                <RevButton variant="secondary" icon onClick={handleSaveAttendees} disabled={savingAttendees}
                                    aria-label={t('eventStats.registration.saveAria')}>
                                    <span className="material-icons rev-icon-sm">{savingAttendees ? 'hourglass_empty' : 'check'}</span>
                                </RevButton>
                                <RevButton variant="secondary" icon onClick={handleCancelAttendees} disabled={savingAttendees}
                                    aria-label={t('eventStats.registration.cancelAria')}>
                                    <span className="material-icons rev-icon-sm">close</span>
                                </RevButton>
                            </div>
                        ) : (
                            <div className="rev-stat__editrow">
                                <RevFigure size="xl">{formatInteger(attendeesValue)}</RevFigure>
                                <RevButton variant="secondary" icon onClick={handleEditAttendees}
                                    aria-label={t('eventStats.registration.editAria')}>
                                    <span className="material-icons rev-icon-sm">edit</span>
                                </RevButton>
                            </div>
                        )}
                        <div className="rev-stat__pie">{t('eventStats.registration.attendeesHelp')}</div>
                    </div>
                </div>
            </RevCard>

            <RevCard titleAs="h3" title={t('eventStats.groups.users.title')}>
                <div className="rev-stat-grid">
                    <StatItem
                        label={t('eventStats.groups.users.access')}
                        value={formatInteger(accessesRaw, props.loadingStats)}
                        helper={`${percentOf(accessesValue, attendeesValue)} ${t('eventStats.pie.ofAttendees')}`}
                    />
                    <StatItem
                        label={t('eventStats.groups.users.matched')}
                        value={formatInteger(matchesRaw, props.loadingStats)}
                        helper={`${percentOf(matchesValue, accessesValue || attendeesValue)} ${accessesValue ? t('eventStats.pie.ofAccess') : t('eventStats.pie.ofAttendees')}`}
                    />
                    <StatItem
                        label={t('eventStats.groups.users.selfies')}
                        value={formatInteger(selfiesRaw, props.loadingStats)}
                        helper={averagePer(selfiesValue, matchesValue || accessesValue || attendeesValue, t('eventStats.pie.perUser'))}
                    />
                </div>
            </RevCard>

            <RevCard titleAs="h3" title={t('eventStats.groups.photos.title')}>
                <div className="rev-stat-grid">
                    <StatItem
                        label={t('eventStats.groups.photos.uploaded')}
                        value={formatInteger(uploadedPhotosRaw, props.loadingStats)}
                        helper={averagePer(uploadedPhotos, attendeesValue, t('eventStats.pie.photoPerAttendee'))}
                        onClick={uploadedPhotos > 0 ? onSetModal : undefined}
                    />
                    <StatItem
                        label={t('eventStats.groups.photos.delivered')}
                        value={formatInteger(deliveredPhotosRaw, props.loadingStats)}
                        helper={averagePer(deliveredPhotos, matchesValue || accessesValue || attendeesValue, t('eventStats.pie.photoPerUser'))}
                    />
                </div>
            </RevCard>

            <RevCard titleAs="h3" title={t('eventStats.groups.sales.title')}>
                <div className="rev-stat-grid">
                    <StatItem
                        label={t('eventStats.groups.sales.orders')}
                        value={formatInteger(numberOfOrders, props.loadingStats)}
                        helper={`${percentOf(numberOfOrders || 0, matchesValue || accessesValue || registeredValue)} ${t('eventStats.pie.ofUsers')} / ${percentOf(numberOfOrders || 0, attendeesValue)} ${t('eventStats.pie.ofAttendees')}`}
                    />
                    <StatItem
                        label={t('eventStats.groups.sales.total')}
                        value={formatCurrency(totalAmountOrders, props.loadingStats)}
                        helper={<>{t('eventStats.groups.sales.avgTicket')} <span className="rev-num">{avgTicketValue !== undefined ? currencyFormatter.format(avgTicketValue) : '—'}</span></>}
                        onClick={totalAmountOrders > 0 ? handleNavigateToPayments : undefined}
                    />
                    <StatItem
                        label={t('eventStats.groups.sales.packsCount')}
                        value={formatInteger(numberOfPackOrders, props.loadingStats)}
                        helper={`${percentOf(numberOfPackOrders || 0, numberOfOrders || 0)} ${t('eventStats.pie.ofTotalOrders')}`}
                    />
                    <StatItem
                        label={t('eventStats.groups.sales.packsTotal')}
                        value={formatCurrency(totalAmountPackOrders, props.loadingStats)}
                        helper={`${percentOf(totalAmountPackOrders || 0, totalAmountOrders || 0)} ${t('eventStats.pie.totalSales')}`}
                    />
                </div>
            </RevCard>

            <div className="rev-stats__bottom">
                <RevCard titleAs="h3" title={t('eventStats.salesSummary.title')}>
                    <RevKvRow k={t('eventStats.salesSummary.orders')} v={formatInteger(numberOfOrders, props.loadingStats)} num />
                    <RevKvRow k={t('eventStats.salesSummary.deliveredPhotos')} v={formatInteger(deliveredPhotosRaw, props.loadingStats)} num />
                    <RevKvRow k={t('eventStats.salesSummary.packs')} v={formatInteger(numberOfPackOrders, props.loadingStats)} num />
                    <RevKvRow k={t('eventStats.salesSummary.avgTicket')} v={avgTicketValue !== undefined ? currencyFormatter.format(avgTicketValue) : '—'} num />
                    <div className="rev-summary-total">
                        <span>{t('eventStats.salesSummary.total')}</span>
                        <RevFigure size="sm">{formatCurrency(totalAmountOrders, props.loadingStats)}</RevFigure>
                    </div>
                </RevCard>

                <RevCard titleAs="h3" title={t('eventStats.recentOrders.title')}>
                    {recentOrdersLoading ? (
                        <RevSkeleton.Cuerpo
                            tipo="filas"
                            cabecera
                            filas={5}
                            celdas={['texto', 'texto', 'texto', 'texto', 'chip']}
                            clases={{ lista: 'rev-order-list', cabecera: 'rev-order-row rev-order-row--head', fila: 'rev-order-row rev-order-row--esqueleto' }}
                            label={t('event.photos.loading')} />
                    ) : recentOrdersError || recentOrders.length === 0 ? (
                        <RevEmpty
                            icon="receipt_long"
                            title={t('eventStats.recentOrders.empty.title')}
                            body={recentOrdersError ? t('eventStats.recentOrders.error') : t('eventStats.recentOrders.empty.body')}
                        />
                    ) : (
                        <div className="rev-order-list">
                            <div className="rev-order-row rev-order-row--head">
                                <span>{t('eventStats.recentOrders.ref')}</span>
                                <span>{t('eventStats.recentOrders.buyer')}</span>
                                <span className="rev-order-row__n">{t('eventStats.recentOrders.photos')}</span>
                                <span className="rev-order-row__amount">{t('eventStats.recentOrders.amount')}</span>
                                <span>{t('eventStats.recentOrders.status')}</span>
                            </div>
                            {recentOrders.map((order) => (
                                <div className="rev-order-row" key={order.orderId}>
                                    <span className="rev-order-row__ref rev-mono">{getOrderNumber(order.orderId)}</span>
                                    <span className="rev-order-row__buyer">{order.customerName || '—'}</span>
                                    <span className="rev-order-row__n rev-num">{order.orderCounter ?? 0}</span>
                                    <span className="rev-order-row__amount rev-num">{formatCurrency(order.orderAmount)}</span>
                                    <span className="rev-order-row__chip">
                                        {order.refundedAmount > 0 ? (
                                            <RevChip tone="warn">{t('eventStats.recentOrders.refunded')}</RevChip>
                                        ) : (
                                            <RevChip tone="ok">{t('eventStats.recentOrders.paid')}</RevChip>
                                        )}
                                        {order.orderType === 'pack' && (
                                            <RevChip tone="info">{t('eventStats.recentOrders.pack')}</RevChip>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </RevCard>
            </div>
        </div>
    );
};

const mapStateToProps = state => (
    {
        numberOfPhotos: state.statsReducer.numberOfPhotos,
        numberOfIndexedFaces: state.statsReducer.numberOfIndexedFaces,
        numberOfUsersAccessByCode: state.statsReducer.numberOfUsersAccessByCode,
        numberOfUsersWithMatch: state.statsReducer.numberOfUsersWithMatch,
        numberOfDeliveredPhotos: state.statsReducer.numberOfDeliveredPhotos,
        numberOfSelfies: state.statsReducer.numberOfSelfies,
        numberOfUniquesSharesByEvent: state.statsReducer.numberOfUniquesSharesByEvent,
        numberOfUniquesViewsByEvent: state.statsReducer.numberOfUniquesViewsByEvent,
        numberOfGlobalViewsByEvent: state.statsReducer.numberOfGlobalViewsByEvent,
        numberOfOrders: state.statsReducer.numberOfOrders,
        numberOfPackOrders: state.statsReducer.numberOfPackOrders,
        totalAmountOrders: state.statsReducer.totalAmountOrders,
        totalAmountPackOrders: state.statsReducer.totalAmountPackOrders,
        soldPhotos: state.statsReducer.soldPhotos,
        photosByUser: state.statsReducer.photosByUser,
        loadingStats: state.statsReducer.loading,
        user: state.usersReducer.user,
        selectedEvent: state.eventsReducer.selectedEvent,
    }
);

const mapDispatchToProps =
{
    getNumberOfDeliveredPhotos,
    getNumberOfIndexedFaces,
    getNumberOfPhotos,
    getNumberOfSelfies,
    getNumberOfUsersAccessByCode,
    getNumberOfUsersWithMatch,
    getNumberOfUniquesSharesByEvent,
    getNumberOfUniquesViewsByEvent,
    getNumberOfGlobalViewsByEvent,
    getNumberOfOrders,
    getNumberOfPackOrders,
    getTotalAmountOrders,
    getTotalAmountPackOrders,
    getSoldPhotos,
    getPhotosByUser,
    cleanEventStats,
    updateEvent,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EventStats));
